import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Spinner } from '@/components/ui/spinner';
import { DatePicker } from '@/components/ui/date-picker';
import { useToast } from '@/hooks/useToast';
import { useGenerateDailySummaryMutation, DailySummaryResponse } from '@/lib/api-extensions/admin-queries';
import { Bell, Calendar, Users, Target } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DailySummaryGeneratorProps {
  className?: string;
  organizationId: number | null;
  businessId: number | null;
}

export function DailySummaryGenerator({ className, organizationId }: DailySummaryGeneratorProps) {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [response, setResponse] = useState<DailySummaryResponse | null>(null);
  const { toast } = useToast();
  
  const generateSummaryMutation = useGenerateDailySummaryMutation();
  
  const getDefaultDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 1);
    return date;
  };
  
  const handleGenerate = async () => {
    try {
      const result = await generateSummaryMutation.mutateAsync({
        organizationId: organizationId,
        targetDate: selectedDate,
      });
      
      setResponse(result);
      
      toast({
        title: 'Success',
        description: result.message,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to generate daily summaries',
        variant: 'destructive',
      });
    }
  };

  return (
    <Card className={cn("w-full max-w-2xl", className)}>
      <CardHeader className="px-6 py-6">
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Generate Daily Summary Notifications
        </CardTitle>
        <CardDescription className="mt-2">
          Create daily summary notifications for users based on their optimization actions.
          If no date is selected, notifications will be generated for yesterday.
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6 pt-6">
        
        {/* Date Picker */}
        <div>
          <label className="text-sm font-medium mb-2 block flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Target Date (optional)
          </label>
          <DatePicker
            date={selectedDate}
            onSelect={setSelectedDate}
            placeholder="Select date (defaults to yesterday)"
            className="w-full"
            showClearButton={true}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Leave empty to generate notifications for yesterday ({getDefaultDate().toLocaleDateString()})
          </p>
        </div>
        
        {/* Generate Button */}
        <Button 
          onClick={handleGenerate}
          disabled={generateSummaryMutation.isPending}
          className="w-full"
        >
          {generateSummaryMutation.isPending ? (
            <>
              <Spinner className="mr-2 h-4 w-4" />
              Generating Notifications...
            </>
          ) : (
            <>
              <Bell className="mr-2 h-4 w-4" />
              Generate Notifications
            </>
          )}
        </Button>
        
        {/* Response Display */}
        {response && (
          <Alert className={response.success ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
            <div className="space-y-3">
              <AlertDescription className="text-sm">
                <strong>{response.message}</strong>
              </AlertDescription>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    <span className="font-medium">Users Processed:</span>
                    <span>{response.users_processed}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Bell className="h-4 w-4" />
                    <span className="font-medium">Notifications Created:</span>
                    <span>{response.notifications_created}</span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    <span className="font-medium">Users with Actions:</span>
                    <span>{response.users_with_actions}</span>
                  </div>
                  {response.errors > 0 && (
                    <div className="flex items-center gap-2 text-red-600">
                      <span className="font-medium">Errors:</span>
                      <span>{response.errors}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {response.success && response.notifications_created > 0 && (
                <div className="mt-3 p-3 bg-green-100 rounded-md text-sm">
                  <p className="text-green-800">
                    📧 {response.notifications_created} daily summary notification{response.notifications_created !== 1 ? 's' : ''} 
                    {response.notifications_created !== 1 ? ' have' : ' has'} been created successfully. 
                    Users will see detailed summaries of their optimization actions in their notifications inbox.
                  </p>
                </div>
              )}
              
              {response.success && response.notifications_created === 0 && (
                <div className="mt-3 p-3 bg-blue-100 rounded-md text-sm">
                  <p className="text-blue-800">
                    ℹ️ No notifications were created. This usually means no users had optimization actions on the selected date.
                  </p>
                </div>
              )}
            </div>
          </Alert>
        )}
        
        {/* Campaign Summaries */}
        {response && response.campaign_summaries && response.campaign_summaries.length > 0 && (
          <Card className="mt-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Target className="h-5 w-5" />
                Campaign Activity Summary
              </CardTitle>
              <CardDescription>
                Campaigns that had notifications generated
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {response.campaign_summaries.map((campaign) => (
                  <div 
                    key={campaign.campaign_id} 
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-md"
                  >
                    <div className="flex-1">
                      <div className="font-medium text-sm">
                        {campaign.action_count} actions for campaign - {campaign.campaign_name}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        in account {campaign.account_name}, for user {campaign.user_name}
                      </div>
                    </div>
                    <div className="text-sm font-medium text-blue-600">
                      {campaign.action_count} action{campaign.action_count !== 1 ? 's' : ''}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}


      </CardContent>
    </Card>
  );
}